import { useMemo, useState } from "react";
import { CATEGORIES, latestPosts } from "@/mocks/melon";

const catColor: Record<string, string> = {
  影视: "bg-primary-100 text-primary-700",
  娱乐圈: "bg-accent-100 text-accent-700",
  综艺: "bg-secondary-100 text-secondary-700",
  主播: "bg-accent-100 text-accent-700",
  社会: "bg-background-200 text-foreground-600",
  音乐: "bg-primary-100 text-primary-700",
};

function LatestGrid() {
  const [cat, setCat] = useState<string>("全部");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    return latestPosts.filter((p) => {
      const okCat = cat === "全部" || p.cat === cat;
      const kw = keyword.trim().toLowerCase();
      const okKw = !kw || p.title.toLowerCase().includes(kw) || p.cat.toLowerCase().includes(kw) || p.excerpt.toLowerCase().includes(kw);
      return okCat && okKw;
    });
  }, [cat, keyword]);

  return (
    <section id="latest" className="scroll-mt-24 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">Fresh Updates</p>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
              新鲜瓜讯速递
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
              每天持续整理的吃瓜速递，带观点、带信源提示，比单纯的榜单多看一层。
            </p>
          </div>

          <label className="flex h-11 w-full max-w-sm items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 transition-colors focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-search-line text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索你想吃的瓜…"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </label>
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`inline-flex h-9 items-center rounded-full px-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                cat === c
                  ? "bg-accent-500 text-foreground-950"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
            <i className="ri-emotion-happy-line text-4xl text-foreground-300" />
            <p className="mt-4 text-base font-semibold text-foreground-800">这个瓜好像还没开拍</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或栏目试试，或者先去热搜榜逛逛。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setCat("全部");
              }}
              className="mt-5 inline-flex h-10 items-center rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              清空筛选
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((p) => (
              <article
                key={p.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-100 transition-colors hover:border-primary-200"
              >
                <div className="relative h-40 overflow-hidden sm:h-44">
                  <img
                    src={p.img}
                    alt={p.alt}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  <span
                    className={`absolute left-3 top-3 rounded px-2 py-0.5 text-[11px] font-semibold ${catColor[p.cat] ?? "bg-background-200 text-foreground-600"}`}
                  >
                    {p.cat}
                  </span>
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[11px] font-medium text-white/95">
                    <i className="ri-time-line text-accent-300" />
                    {p.time}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    <a href="#topic">{p.title}</a>
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{p.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" /> {p.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line" /> {p.comments}
                    </span>
                    <span className="whitespace-nowrap">{p.date}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default LatestGrid;